import { RouteLegDTO, RoutePackageDTO, RoutePlanTbLevelEnum } from 'api/model';

/**
 * Språket för TB på paketkortet: ordet i chippet, meningen som förklarar ordet, och de avrundade
 * beloppen i hoverns uträkning.
 *
 * Varför ord i stället för tal: intäkten är prislistepris eller schablon (300 kr/h), kostnaden är
 * uppskattad tid × schablontimlön (227 kr, +45 kr på OB-tid). Ett tal med decimal lovar en
 * precision underlaget inte har. Talen finns kvar — i hovern, avrundade — så den som ska
 * ifrågasätta ett förslag kan göra det.
 *
 * Se .claude/features/route-planning.md ("TB-språket i kortet").
 */

/** Schablontimlön i TB-kalkylen — PriceCostLogic.DefaultAverageSalaryPerHourIncludingSocialCosts. */
export const STANDARD_HOURLY_COST_SEK = 227;

/** OB-tillägg per timme utanför 06–19 — PriceCostLogic.DefaultObPerHourIncludingSocialCosts. */
export const STANDARD_OB_SURCHARGE_SEK = 45;

/** Schablonintäkten när prislistan saknar rad — PriceCostLogic.FallbackPricePerHour. */
export const FALLBACK_REVENUE_PER_HOUR_SEK = 300;

/** Ordet i chippet. Ersätter "TB +65,3 %". */
export const tbClassLabel = (level: RoutePlanTbLevelEnum): string => {
  switch (level) {
    case RoutePlanTbLevelEnum.Green:
      return 'God marginal';
    case RoutePlanTbLevelEnum.Yellow:
      return 'Tunn marginal';
    case RoutePlanTbLevelEnum.Red:
      return 'Går back';
    default:
      return 'Marginal okänd';
  }
};

/**
 * Hoverns första rad: ordet som mening. Ett ord i ett chip är alltid tvetydigt första gången —
 * särskilt på uppdragsraden, där headerns "TB"-etikett inte står intill.
 */
export const tbClassMeaning = (level: RoutePlanTbLevelEnum): string => {
  switch (level) {
    case RoutePlanTbLevelEnum.Green:
      return 'Täckningsbidrag: intäkten täcker kostnaden med god marginal.';
    case RoutePlanTbLevelEnum.Yellow:
      return 'Täckningsbidrag: intäkten täcker kostnaden, men med liten marginal.';
    case RoutePlanTbLevelEnum.Red:
      return 'Täckningsbidrag: kostnaden är högre än intäkten.';
    default:
      return 'Täckningsbidrag kan inte beräknas — prislistan saknar rad för sträckan.';
  }
};

/** Avrundning till närmaste hundralapp. Golvet för alla belopp i ruttplaneringen. */
export const roundSek = (value: number): number => Math.round(value / 100) * 100;

const formatSekValue = (value: number): string =>
  `${value < 0 ? '−' : ''}${Math.abs(value).toLocaleString('sv-SE')} kr`;

/** "≈ 5 200 kr". Tecknet ≈ säger att talet är ett estimat, inte ett mätvärde. */
export const formatSekApprox = (value: number): string =>
  `≈ ${formatSekValue(roundSek(value))}`;

/** "~65 %" — bufferten kommer från backend, klienten räknar aldrig om den. */
export const formatBufferPercent = (bufferPercent: number): string => {
  const rounded = Math.round(bufferPercent);
  return `~${rounded > 0 ? '+' : ''}${rounded} %`;
};

export interface TbBreakdown {
  revenueSek: number;
  costSek: number;
  remedyRevenueSek: number;
  remedyCostSek: number;
  tbSek: number;
}

/**
 * Uträkningen som visas i hovern, avrundad så att SUBTRAKTIONEN GÅR IHOP: TB räknas ur de
 * avrundade talen, inte ur de exakta. Annars står det en subtraktion i hovern som inte stämmer
 * (5 200 − 1 800 = 3 400, men "3 417"), och det är precis den sortens detalj som gör att någon
 * slutar lita på hela kortet.
 */
export const tbBreakdown = (
  revenueSek: number,
  costSek: number,
  remedyRevenueSek = 0,
  remedyCostSek = 0
): TbBreakdown => {
  const revenue = roundSek(revenueSek);
  const cost = roundSek(costSek);
  const remedyRevenue = roundSek(remedyRevenueSek);
  const remedyCost = roundSek(remedyCostSek);
  return {
    revenueSek: revenue,
    costSek: cost,
    remedyRevenueSek: remedyRevenue,
    remedyCostSek: remedyCost,
    tbSek: revenue + remedyRevenue - cost - remedyCost,
  };
};

/**
 * Tidsunderlaget bakom ett bens schablonkostnad: benets fönster (rasten ligger INNE i det) plus
 * överföringen in till benet, minus reserverad rast. Exakt det spann `LegCostSek` prissätter — och
 * därför det enda tal som får stå som källa intill kostnaden. Paketets `TotalOccupiedMs` duger inte:
 * den summerar uppdragens arbetstid utan överföringar, och rasten hanteras separat.
 */
export const legCostBasisMs = (leg: RouteLegDTO): number =>
  Math.max(
    0,
    new Date(leg.plannedEnd).getTime() -
      new Date(leg.plannedStart).getTime() +
      leg.transferFromPreviousMs -
      leg.breakMinutes * 60_000
  );

/** Paketets kostnadsunderlag = summan av benens, så hovern och kostnaden räknar på samma spann. */
export const packageCostBasisMs = (pkg: RoutePackageDTO): number =>
  pkg.legs.reduce((total, leg) => total + legCostBasisMs(leg), 0);

export interface TbUncertaintyInput {
  /** Intäkten är schablon (300 kr/h) i stället för prislistepris. */
  revenueIsEstimated: boolean;
  /** Minst ett uppdrags arbetstid är uppskattad, inte bokad. */
  durationIsEstimated: boolean;
  /** 'package' = hela paketets TB (valda lucka-åtgärder ingår), 'leg' = ett uppdrags eget TB. */
  scope: 'package' | 'leg';
  /** Hotellnatt krävs men går inte att prissätta i planeringen. */
  hotelUnpriced?: boolean;
  /** En vald lucka-åtgärd saknar prisrad, så paketets TB inte kan beräknas. */
  unpricedRemedy?: boolean;
}

/**
 * Hoverns tredje lager. Det här är raden som skiljer "grovt" från "luddigt": den som ska ta
 * beslutet får veta hur mycket klassen väger, i stället för att gissa.
 */
export const tbUncertaintyNotes = ({
  revenueIsEstimated,
  durationIsEstimated,
  scope,
  hotelUnpriced,
  unpricedRemedy,
}: TbUncertaintyInput): string[] => {
  const notes: string[] = [];

  if (unpricedRemedy) {
    notes.push(
      'Den valda lucka-åtgärden saknar prisrad — därför kan paketets TB inte beräknas.'
    );
  }

  if (revenueIsEstimated) {
    notes.push(
      `Intäkten är schablon (${FALLBACK_REVENUE_PER_HOUR_SEK} kr/h) — prislistan saknar rad för sträckan eller datumet. Klassen kan därför aldrig bli grön.`
    );
  }

  if (durationIsEstimated) {
    notes.push(
      'Arbetstiden är uppskattad, inte bokad (±44 min ≈ ±3 procentenheter på bufferten).'
    );
  }

  notes.push(
    `Timlönen är schablon för alla fälttestare (${STANDARD_HOURLY_COST_SEK} kr/h, +${STANDARD_OB_SURCHARGE_SEK} kr/h på OB-tid utanför 06–19).`
  );

  // Hotellnatten har ingen kostnad i planeringen. Att den inte går att räkna in är precis därför
  // den måste sägas — och därför den kapar grönt (se tb-remedies.computeTbLevel).
  if (hotelUnpriced) {
    notes.push(
      'Hotellnatt krävs och är inte prissatt i planeringen — klassen kan därför inte bli grön.'
    );
  }

  // Benets egen TB vet inget om luckorna: åtgärderna hör till paketet, inte till uppdraget.
  if (scope === 'leg') {
    notes.push(
      'Lucka-åtgärder och hotellnätter hör till paketet, inte till det enskilda uppdraget.'
    );
  }

  return notes;
};
